import { useEffect, useRef } from 'react';
import type { Virtualizer } from '@tanstack/react-virtual';
import type { TimelineRow } from '../types';

interface Params {
  rows: readonly TimelineRow[];
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  /** Permalink target, or null for the live tail. */
  anchorId: string | null;
  ready: boolean;
}

/**
 * Scrolls a permalink target into view, exactly once per anchor.
 *
 * Guarded on the row EXISTING rather than on the query settling: the anchored
 * page arrives before the virtualiser has measured anything, and calling
 * `scrollToIndex` against an index that is not in `rows` yet silently does
 * nothing — leaving the reader at the bottom of the window with no explanation.
 */
export function useTimelineAnchor({ rows, virtualizer, anchorId, ready }: Params): void {
  const jumpedTo = useRef<string | null>(null);

  useEffect(() => {
    if (!ready || anchorId === null || jumpedTo.current === anchorId) return;
    const index = rows.findIndex((row) => row.id === anchorId);
    if (index < 0) return;
    jumpedTo.current = anchorId;
    virtualizer.scrollToIndex(index, { align: 'center' });
  }, [anchorId, ready, rows, virtualizer]);
}
